import {
    Box,
    Center,
    Container,
    Flex,
    HStack,
    Heading,
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Skeleton,
    Slide,
    Text,
    VStack,
    useModalContext,
} from '@chakra-ui/react';
import {
    FaIcon, PaginationArrow, ThemeTokens, usePaginationLabel,
} from '@acp/core-ui';
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useAgencySwitcher, useSession } from '@acp/feature/auth';
import { PortfolioItem } from './portfolio-item';
import { ImageContentLayout } from '../../../layouts';
import { DiLogo } from '../../di-logo';

const PORTFOLIO_PAGE_SIZE = 5;

function PortfolioList() {
    const navigate = useNavigate();
    const { complianceAgencyId } = useSession();
    const { onClose: closeSwitchPortfolioPanel } = useModalContext();

    const [switchingAgencyId, setSwitchingAgencyId] = useState<number | null>(null);

    const paginationProps = usePaginationLabel({
        defaultPage: 1,
        pageSize: PORTFOLIO_PAGE_SIZE,
    });

    const {
        isLoading, userAgencies, switchUserPortfolio, isSwitchingPortfolio,
    } = useAgencySwitcher({
        onSuccess: () => {
            // return to home page
            closeSwitchPortfolioPanel();
            navigate('/', {
                replace: true,
            });
        },
        onFailure: () => {
            closeSwitchPortfolioPanel();
            navigate('/auth/error?message=Failed to switch portfolio', {
                replace: true,
            });
        },
    });

    useEffect(() => {
        if (userAgencies === undefined) return;

        paginationProps.setTotalResults(userAgencies.length);
    }, [userAgencies]);

    const handleSwitchUserPortfolio = (agencyId: number) => {
        setSwitchingAgencyId(agencyId);
        switchUserPortfolio(agencyId);
    };

    const sortedUserAgencies = useMemo(() => {
        if (!userAgencies) return [];

        // we want the current portfolio to be the first item in the list
        const sorted = userAgencies.sort((a, b) => {
            if (a.complianceAgencyId === complianceAgencyId) {
                return -1;
            }
            if (b.complianceAgencyId === complianceAgencyId) {
                return 1;
            }
            return 0;
        });

        const start = (paginationProps.currentPage - 1) * PORTFOLIO_PAGE_SIZE;
        const end = paginationProps.currentPage * PORTFOLIO_PAGE_SIZE;

        return sorted.slice(start, end);
    }, [userAgencies, paginationProps.currentPage, complianceAgencyId]);

    if (isLoading || userAgencies === undefined) {
        return (
            <VStack spacing={3}>
                <Skeleton p={6} h={20} w="full" />
                <Skeleton p={6} h={20} w="full" />
                <Skeleton p={6} h={20} w="full" />
            </VStack>
        );
    }

    return (
        <>
            {userAgencies.length > PORTFOLIO_PAGE_SIZE
                && (
                    <HStack justifyContent="flex-end" mb={2}>
                        <PaginationArrow {...paginationProps} />
                    </HStack>
                )}

            {sortedUserAgencies.map((userAgency) => (
                <PortfolioItem
                    key={userAgency.complianceAgencyName}
                    agencyId={userAgency.complianceAgencyId}
                    agencyName={userAgency.complianceAgencyName}
                    isCurrentPortfolio={
                        userAgency.complianceAgencyId === complianceAgencyId
                    }
                    onChangePortfolio={handleSwitchUserPortfolio}
                    onCloseSwitchPortfolioPanel={closeSwitchPortfolioPanel}
                    isSwitching={isSwitchingPortfolio}
                    switchingAgencyId={switchingAgencyId}
                />
            ))}
        </>
    );
}

export function SwitchPortfolioPanel() {
    return (
        <Flex flexDir="column" alignItems="start" width="100%">
            <Heading fontSize="3xl">Switch to a different portfolio</Heading>
            <Text fontSize="lg" py={6}>
                If you can not see a portfolio that you think you should have access to,
                please contact your Detector Inspector Account Manager.
            </Text>
            <ModalBody p={0} w="full">
                <PortfolioList />
            </ModalBody>
        </Flex>
    );
}

function DesktopSwitchPortfolioPanel({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) {
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="full"
            isCentered
            scrollBehavior="outside"
        >
            <ModalOverlay />
            <ModalContent containerProps={{
                display: 'block',
            }}
            >
                <ImageContentLayout>
                    <SwitchPortfolioPanel />
                </ImageContentLayout>
                <ModalCloseButton />
            </ModalContent>
        </Modal>
    );
}

function MobileSwitchPortfolioPanel({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}) {
    return (
        <Slide direction="left" in={isOpen} style={{ zIndex: 10 }}>
            <Box w="full" h="full" bg="white" overflowY="auto">
                <HStack
                    justifyContent="center"
                    w="full"
                    bg="white"
                    borderBottomWidth={1}
                    px={ThemeTokens.Defaults.px}
                    py={5}
                >
                    <IconButton
                        colorScheme="muted"
                        onClick={onClose}
                        variant="ghost"
                        aria-label="menu"
                        icon={<FaIcon icon={faChevronLeft} />}
                        position="absolute"
                        left={3}
                        size="sm"
                        color="muted.500"
                    />

                    <DiLogo variant="horizontalRGB" w="10rem" />
                </HStack>

                <Center minH="100%" w="100%" px={{ base: 5, sm: 10 }} py={10}>
                    <Container maxW="xl">
                        <SwitchPortfolioPanel />
                    </Container>
                </Center>
            </Box>
        </Slide>
    );
}

SwitchPortfolioPanel.Desktop = DesktopSwitchPortfolioPanel;
SwitchPortfolioPanel.Mobile = MobileSwitchPortfolioPanel;
